import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { integrationSolutionKeys } from "@/lib/query";

export type LinkedSolution = {
  nymblSolutionId: string;
  solutionId: string;
  name: string;
  isActive: boolean;
  lastSyncedAt: string;
};

export type AvailableSolution = {
  nymblSolutionId: string;
  name: string;
  isActive: boolean;
  nymblAccountId: string | null;
  lastSyncedAt: string;
};

export type IntegrationSolutionsResponse = {
  configured: boolean;
  linked: LinkedSolution[];
  available: AvailableSolution[];
};

export type RefreshSolutionsResult = {
  fetched: number;
  linkedUpdated: number;
  linkedUnchanged: number;
  newDiscovered: number;
  errors: number;
  constraintsSolutionsProcessed: number;
  constraintsFetched: number;
  constraintsCreated: number;
  constraintLinksCreated: number;
  constraintLinksPruned: number;
  constraintFitScoreRecomputed: number;
  constraintErrors: number;
  accountConstraintsSolutionsProcessed: number;
  accountConstraintsFetched: number;
  accountConstraintsUnmapped: number;
  accountConstraintsCreated: number;
  accountConstraintsRenamed: number;
  accountConstraintLinksCreated: number;
  accountConstraintLinksPruned: number;
  accountConstraintFitScoreRecomputed: number;
  accountConstraintErrors: number;
};

export type ImportSolutionsResult = {
  discovered: number;
  accountsCreated: number;
  solutionsCreated: number;
  linkedExisting: number;
  skipped: number;
  errors: number;
};

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

export function useIntegrationSolutions() {
  return useQuery({
    queryKey: integrationSolutionKeys.list(),
    queryFn: () => jsonFetch<IntegrationSolutionsResponse>("/api/integrations/solutions"),
  });
}

export function useRefreshSolutions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      jsonFetch<RefreshSolutionsResult>("/api/integrations/solutions/refresh", { method: "POST" }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: integrationSolutionKeys.all });
      const totalErrors = r.errors + r.constraintErrors + r.accountConstraintErrors;
      const totalConstraintLinks = r.constraintLinksCreated + r.accountConstraintLinksCreated;
      toast.success(
        `Refreshed — ${r.fetched} fetched, ${r.linkedUpdated} updated, ${r.newDiscovered} new` +
          (totalConstraintLinks > 0 ? `, ${totalConstraintLinks} constraint link(s)` : "") +
          (r.accountConstraintsRenamed > 0
            ? `, ${r.accountConstraintsRenamed} constraint(s) renamed`
            : "") +
          (totalErrors > 0 ? `, ${totalErrors} errors` : ""),
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useImportSolutions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      jsonFetch<ImportSolutionsResult>("/api/integrations/solutions/import", { method: "POST" }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: integrationSolutionKeys.all });
      // The promoted Solutions appear on the Solutions dashboard.
      qc.invalidateQueries({ queryKey: ["dashboard-solutions"] });
      const dupNote = r.linkedExisting > 0 ? `, ${r.linkedExisting} matched existing` : "";
      const skipNote = r.skipped > 0 ? `, ${r.skipped} skipped` : "";
      toast.success(
        `Imported — ${r.solutionsCreated} Solutions, ${r.accountsCreated} Accounts created` +
          dupNote +
          skipNote +
          (r.errors > 0 ? `, ${r.errors} errors` : ""),
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
